import { useRef } from "react";
import type { StudentQuestionAnswer, StudentTestExercise } from "p6m-p6u";
import UnderlinedInput from "../../../basic/underlinedInput/UnderlinedInput";
import useBreakpoints from "../../../hooks/useBreakpoints";
import ExerciseWrapper from "./ExerciseWrapper";
import useExerciseAnswers from "../../../hooks/useExerciseAnswers";
import { focusNextOrBlur, getExerciseProgress } from "../../../helpers/OnlineTestHelper";

interface StandardExerciseProps {
    exercise: StudentTestExercise;
    exerciseIndex: number;
    studentAnswer: StudentQuestionAnswer;
    onAnswerUpdate: (updatedAnswer: StudentQuestionAnswer, exerciseId: string) => void;
}

const StandardExercise = (props: StandardExerciseProps) => {
    const { isAboveMD } = useBreakpoints();
    const { exerciseId, answers, updateItemAtPosition } = useExerciseAnswers({
        exercise: props.exercise,
        studentAnswer: props.studentAnswer,
        onAnswerUpdate: props.onAnswerUpdate,
    });
    const progress = getExerciseProgress(props.studentAnswer, props.exercise.questionMode);

    const inputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
    const inputRefCallbacks = useRef<((el: HTMLTextAreaElement | null) => void)[]>([]);

    const handleEnter = (index: number) => focusNextOrBlur(inputRefs.current, index);

    const getInputRef = (index: number) => {
        if (!inputRefCallbacks.current[index]) {
            inputRefCallbacks.current[index] = (el) => {
                inputRefs.current[index] = el;
            };
        }
        return inputRefCallbacks.current[index];
    };

    if (!exerciseId) return null;

    const renderLabel = (text: string, className?: string) => {
        return <p className={`font-semibold text-m text-p6-grey-dark px-s ${className ?? ""}`}>{text}</p>;
    };

    const renderInput = (index: number) => {
        return (
            <UnderlinedInput
                data-testid={`online-test-standard-input-${index}`}
                inputRef={getInputRef(index)}
                text={answers[index]?.studentAnswer ?? ""}
                setText={(value) => updateItemAtPosition(index, value)}
                maxLines={1}
                onEnter={() => handleEnter(index)}
            />
        );
    };

    const renderDesktopTable = () => {
        if (!answers.length) return null;

        return (
            <div className="grid grid-cols-2 rounded-medium overflow-hidden">
                {answers.map((item, index) => {
                    const isLast = index === answers.length - 1;
                    const borderBottom = isLast ? "" : "border-b border-p6-grey-lightest";
                    const key = item.id ?? index;
                    const labelFirst = !!item.question;

                    const label = renderLabel(item.question || item.answer || "", "py-xs");

                    const leftContent = labelFirst ? label : renderInput(index);
                    const rightContent = labelFirst ? renderInput(index) : label;

                    return (
                        <div
                            key={key}
                            className={`col-span-2 grid grid-cols-2 ${borderBottom}`}
                        >
                            <div
                                className={
                                    labelFirst
                                        ? "px-s py-m border-r border-p6-grey-lightest"
                                        : "px-l py-m border-r border-p6-grey-lightest"
                                }
                            >
                                {leftContent}
                            </div>
                            <div className={labelFirst ? "px-l py-m" : "px-s py-m"}>{rightContent}</div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMobileCards = () => {
        if (!answers.length) return null;

        return (
            <div className="flex flex-col gap-m">
                {answers.map((item, index) => {
                    const labelFirst = !!item.question;

                    const label = renderLabel(item.question || item.answer || "");

                    return (
                        <div
                            key={item.id ?? index}
                            className="bg-white border border-p6-grey-light rounded-medium p-m"
                        >
                            <div className="flex flex-col gap-s">
                                {labelFirst ? label : renderInput(index)}
                                {labelFirst ? renderInput(index) : label}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <ExerciseWrapper
            data-testid="online-test-exercise-standard"
            number={props.exerciseIndex + 1}
            title={props.exercise.extraComments ?? ""}
            progress={progress}
        >
            {isAboveMD ? renderDesktopTable() : renderMobileCards()}
        </ExerciseWrapper>
    );
};

export default StandardExercise;
